import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PageLayout, PageHeader } from '../../components'
import { StatusChip, LoadingSpinner, ToastNotification } from '../../components/atoms'
import { useToast } from '../../hooks'
import { authenticatedFetch, authenticatedFetchJson } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import logger from '../../utils/logger'
import styles from './AdminBillingUnlinkedAccounts.module.scss'

const SEARCH_DEBOUNCE_MS = 400

const STATUS_COLORS = {
  ready: 'success',
  already_linked: 'neutral',
  no_match: 'warning',
  stripe_not_found: 'error',
  conflict: 'warning'
}

const STATUS_LABELS = {
  ready: 'Ready to Link',
  already_linked: 'Already Linked',
  no_match: 'No Match',
  stripe_not_found: 'Stripe Not Found',
  conflict: 'Conflict'
}

const CsvLinkDialog = ({ open, onClose, onComplete, showToast }) => {
  const [phase, setPhase] = useState('upload') // upload | preview | linking | done
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState(null)
  const [selections, setSelections] = useState({})
  const [linkingProgress, setLinkingProgress] = useState(false)
  const [results, setResults] = useState(null)
  const fileInputRef = React.useRef(null)

  const resetState = useCallback(() => {
    setPhase('upload')
    setUploading(false)
    setPreview(null)
    setSelections({})
    setLinkingProgress(false)
    setResults(null)
  }, [])

  const handleClose = useCallback(() => {
    resetState()
    onClose()
  }, [resetState, onClose])

  const handleFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const response = await authenticatedFetch(apiEndpoints.billingCsvLinkPreview, {
        method: 'POST',
        body: formData
      })
      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error(err.message || `Upload failed (${response.status})`)
      }
      const data = await response.json()
      setPreview(data?.data || null)
      // Pre-check all "ready" rows
      const initial = {}
      ;(data?.data?.rows || []).forEach((row, idx) => {
        if (row.status === 'ready') initial[idx] = true
      })
      setSelections(initial)
      setPhase('preview')
    } catch (err) {
      logger.error('CSV preview failed:', err)
      showToast(err.message || 'Failed to parse CSV', 'error')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [showToast])

  const selectedCount = Object.values(selections).filter(Boolean).length

  const toggleAll = useCallback((checked) => {
    const updated = {}
    ;(preview?.rows || []).forEach((row, idx) => {
      if (row.status === 'ready') updated[idx] = checked
    })
    setSelections(updated)
  }, [preview])

  const handleConfirmLink = useCallback(async () => {
    const links = (preview?.rows || [])
      .filter((_, idx) => selections[idx])
      .map(row => ({
        client_account_id: row.client_account_id,
        stripe_customer_id: row.stripe_customer_id
      }))

    if (links.length === 0) {
      showToast('No rows selected', 'warning')
      return
    }

    setLinkingProgress(true)
    setPhase('linking')
    try {
      const res = await authenticatedFetchJson(apiEndpoints.billingCsvLinkConfirm, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ links })
      })
      setResults(res?.data || null)
      setPhase('done')
      showToast(`Linked ${res?.data?.linked || 0} accounts`, 'success')
      if (onComplete) onComplete()
    } catch (err) {
      logger.error('CSV link confirm failed:', err)
      showToast(err.message || 'Linking failed', 'error')
      setPhase('preview')
    } finally {
      setLinkingProgress(false)
    }
  }, [preview, selections, showToast, onComplete])

  if (!open) return null

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={`${styles.modal} ${phase !== 'upload' ? styles.modalWide : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Import CSV \u2014 Link Stripe Customers</h3>
          <button type='button' className={styles.modalClose} onClick={handleClose}>
            <FontAwesomeIcon icon='times' />
          </button>
        </div>
        <div className={styles.modalBody}>

          {/* Phase: Upload */}
          {phase === 'upload' && (
            <div className={styles.csvUploadZone}>
              <p className={styles.csvUploadHint}>
                Upload a CSV with company names and Stripe customer IDs.<br />
                Headers should contain &quot;name&quot;/&quot;company&quot; and &quot;stripe&quot;/&quot;id&quot;.
              </p>
              <input
                ref={fileInputRef}
                type='file'
                accept='.csv,.tsv,.txt'
                onChange={handleFileSelect}
                className={styles.csvFileInput}
                disabled={uploading}
              />
              {uploading && <LoadingSpinner message='Parsing CSV and validating Stripe IDs...' />}
            </div>
          )}

          {/* Phase: Preview */}
          {phase === 'preview' && preview && (
            <>
              <div className={styles.csvSummaryBar}>
                <span className={styles.csvSummaryItem}>{preview.summary?.total || 0} rows</span>
                <span className={`${styles.csvSummaryItem} ${styles.csvReady}`}>{preview.summary?.ready || 0} ready</span>
                <span className={styles.csvSummaryItem}>{preview.summary?.already_linked || 0} already linked</span>
                <span className={styles.csvSummaryItem}>{preview.summary?.no_match || 0} no match</span>
                {(preview.summary?.stripe_not_found || 0) > 0 && (
                  <span className={`${styles.csvSummaryItem} ${styles.csvError}`}>{preview.summary.stripe_not_found} Stripe not found</span>
                )}
              </div>

              <div className={styles.csvTableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <input
                          type='checkbox'
                          checked={selectedCount === (preview.summary?.ready || 0) && selectedCount > 0}
                          onChange={(e) => toggleAll(e.target.checked)}
                        />
                      </th>
                      <th>CSV Name</th>
                      <th>Matched Account</th>
                      <th>Stripe ID</th>
                      <th>Stripe Name</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.rows || []).map((row, idx) => (
                      <tr key={idx} className={row.status !== 'ready' ? styles.csvRowDisabled : ''}>
                        <td>
                          {row.status === 'ready' && (
                            <input
                              type='checkbox'
                              checked={!!selections[idx]}
                              onChange={(e) => setSelections(prev => ({ ...prev, [idx]: e.target.checked }))}
                            />
                          )}
                        </td>
                        <td>{row.csv_name}</td>
                        <td>{row.client_account_name || '\u2014'}</td>
                        <td className={styles.monoText}>{row.stripe_customer_id}</td>
                        <td>{row.stripe_customer_name || '\u2014'}</td>
                        <td>
                          <StatusChip
                            label={STATUS_LABELS[row.status] || row.status}
                            color={STATUS_COLORS[row.status] || 'neutral'}
                            size='small'
                          />
                          {row.status === 'conflict' && row.existing_stripe_id && (
                            <span className={styles.conflictHint}>Currently: {row.existing_stripe_id}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={styles.csvActions}>
                <button type='button' className={styles.createBtn} onClick={() => { resetState(); setPhase('upload') }}>
                  <FontAwesomeIcon icon='arrow-left' /> Back
                </button>
                <button
                  type='button'
                  className={styles.linkBtn}
                  disabled={selectedCount === 0 || linkingProgress}
                  onClick={handleConfirmLink}
                >
                  <FontAwesomeIcon icon='link' /> Link Selected ({selectedCount})
                </button>
              </div>
            </>
          )}

          {/* Phase: Linking */}
          {phase === 'linking' && (
            <LoadingSpinner message={`Linking ${selectedCount} accounts to Stripe...`} />
          )}

          {/* Phase: Done */}
          {phase === 'done' && results && (
            <div className={styles.csvResultsSummary}>
              <FontAwesomeIcon icon='check-circle' className={styles.csvResultsIcon} />
              <h4>Import Complete</h4>
              <div className={styles.syncGrid}>
                <div className={styles.syncStat}><span className={styles.syncNum}>{results.linked || 0}</span> linked</div>
                <div className={styles.syncStat}><span className={styles.syncNum}>{results.skipped || 0}</span> skipped</div>
                {(results.errors || 0) > 0 && (
                  <div className={styles.syncStat}><span className={styles.syncNumError}>{results.errors}</span> errors</div>
                )}
              </div>
              <button type='button' className={styles.linkBtn} onClick={handleClose} style={{ marginTop: 16 }}>
                Done
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

CsvLinkDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired
}

const AdminBillingUnlinkedAccounts = ({ embedded = false }) => {
  const { toast, showToast, closeToast } = useToast()
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)

  const [linkDialogAccount, setLinkDialogAccount] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [linking, setLinking] = useState(false)
  const [creatingProfile, setCreatingProfile] = useState(null)
  const [searchFilter, setSearchFilter] = useState('')
  const [csvDialogOpen, setCsvDialogOpen] = useState(false)

  const fetchAccounts = useCallback(async (p = page) => {
    try {
      setLoading(true)
      setError(null)
      const response = await authenticatedFetchJson(`${apiEndpoints.billingUnlinkedAccounts}?page=${p}&limit=25`)
      setAccounts(response?.data || [])
      setTotalPages(response?.meta?.total_pages || 1)
      setTotal(response?.meta?.total || 0)
    } catch (err) {
      logger.error('Failed to load unlinked accounts:', err)
      setError(err.message || 'Failed to load unlinked accounts')
    } finally {
      setLoading(false)
    }
  }, [page])

  useEffect(() => { fetchAccounts(page) }, [fetchAccounts, page])

  // Debounced Stripe search
  useEffect(() => {
    if (!linkDialogAccount) return
    const term = searchQuery.trim()
    if (term.length < 2) {
      setSearchResults([])
      return undefined
    }
    let cancelled = false
    const t = setTimeout(async () => {
      try {
        setSearching(true)
        const url = `${apiEndpoints.billingSearchStripeCustomers}?q=${encodeURIComponent(term)}`
        const response = await authenticatedFetchJson(url)
        if (!cancelled) setSearchResults(response?.data || [])
      } catch (err) {
        if (!cancelled) {
          logger.error('Stripe search failed:', err)
          setSearchResults([])
        }
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, SEARCH_DEBOUNCE_MS)
    return () => { cancelled = true; clearTimeout(t) }
  }, [searchQuery, linkDialogAccount])

  const openLinkDialog = useCallback((account) => {
    setLinkDialogAccount(account)
    setSearchQuery(account.account_name) // pre-fill with account name
    setSearchResults([])
  }, [])

  const closeLinkDialog = useCallback(() => {
    setLinkDialogAccount(null)
    setSearchQuery('')
    setSearchResults([])
  }, [])

  const handleLink = useCallback(async (stripeCustomerId, accountOverride) => {
    const target = accountOverride || linkDialogAccount
    if (!target) return
    try {
      setLinking(true)
      await authenticatedFetchJson(
        apiEndpoints.billingLinkAccount(target.id),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stripe_customer_id: stripeCustomerId })
        }
      )
      showToast(`Linked "${target.account_name}" → ${stripeCustomerId}`, 'success')
      closeLinkDialog()
      fetchAccounts()
    } catch (err) {
      logger.error('Link failed:', err)
      showToast(`Link failed: ${err.message || 'unknown error'}`, 'error')
    } finally {
      setLinking(false)
    }
  }, [linkDialogAccount, closeLinkDialog, fetchAccounts, showToast])

  const handleCreateProfile = useCallback(async (acc, action) => {
    setCreatingProfile(acc.id)
    try {
      if (action === 'create_new') {
        await authenticatedFetchJson(apiEndpoints.billingSetup, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            campaign_name: acc.campaign_name || acc.account_name,
            country_code: 'CA',
            preferred_payment_method: 'credit_card',
            stripe_setup_type: 'new'
          })
        })
        showToast(`Created billing profile for "${acc.account_name}"`, 'success')
      } else {
        // Just create a billing profile without Stripe
        await authenticatedFetchJson(apiEndpoints.billingSetup, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            campaign_name: acc.campaign_name || acc.account_name,
            country_code: 'CA',
            preferred_payment_method: 'credit_card'
          })
        })
        showToast(`Created billing profile for "${acc.account_name}"`, 'success')
      }
      fetchAccounts(page)
    } catch (err) {
      logger.error('Create profile failed:', err)
      showToast(`Failed: ${err.message || 'unknown error'}`, 'error')
    } finally {
      setCreatingProfile(null)
    }
  }, [fetchAccounts, page, showToast])

  if (loading) {
    if (embedded) return <LoadingSpinner message='Loading unlinked accounts...' />
    return (
      <PageLayout>
        <PageHeader title='Unlinked Accounts' subtitle='Client accounts not yet linked to a Stripe customer' />
        <LoadingSpinner message='Loading unlinked accounts...' />
      </PageLayout>
    )
  }

  if (error) {
    if (embedded) return <div className={styles.errorState}><p>{error}</p></div>
    return (
      <PageLayout>
        <PageHeader title='Unlinked Accounts' subtitle='Client accounts not yet linked to a Stripe customer' />
        <div className={styles.errorState}><p>{error}</p></div>
      </PageLayout>
    )
  }

  const content = (
    <>

      <div className={styles.topBar}>
        <div className={styles.summary}>
          {total === 0
            ? 'All billable client accounts are linked to Stripe customers.'
            : `${total} client ${total === 1 ? 'account is' : 'accounts are'} not linked.`}
        </div>
        <button
          type='button'
          className={styles.linkBtn}
          onClick={() => setCsvDialogOpen(true)}
          data-testid='import-csv-btn'
        >
          <FontAwesomeIcon icon='download' /> Import CSV
        </button>
      </div>

      {accounts.length > 0 && (
        <>
          <div className={styles.searchBar}>
            <FontAwesomeIcon icon='search' className={styles.searchIcon} />
            <input
              type='text'
              className={styles.searchInput}
              placeholder='Search accounts...'
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              data-testid='search-unlinked'
            />
          </div>
        </>
      )}

      {accounts.length > 0 && (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Client Account</th>
                <th>Suggested Stripe Candidates</th>
                <th className={styles.actionsCol}>Action</th>
              </tr>
            </thead>
            <tbody>
              {accounts.filter(acc => !searchFilter || acc.account_name.toLowerCase().includes(searchFilter.toLowerCase())).map(acc => (
                <tr key={acc.id} data-testid='unlinked-row'>
                  <td className={styles.accountName}>{acc.account_name}</td>
                  <td>
                    {acc.suggested_candidates && acc.suggested_candidates.length > 0
                      ? (
                        <div className={styles.candidates}>
                          {acc.suggested_candidates.map(c => (
                            <div key={c.review_id} className={styles.candidate}>
                              <div className={styles.candidateMain}>
                                <span className={styles.candidateName}>{c.name || '(no name)'}</span>
                                <StatusChip
                                  label={c.reason}
                                  color={c.reason === 'ambiguous' ? 'warning' : 'neutral'}
                                  size='small'
                                />
                              </div>
                              <div className={styles.candidateMeta}>
                                {c.email || '(no email)'} · {c.stripe_customer_id}
                              </div>
                              <button
                                type='button'
                                className={styles.candidateLinkBtn}
                                onClick={() => handleLink(c.stripe_customer_id, acc)}
                              >
                                Link this one
                              </button>
                            </div>
                          ))}
                        </div>
                      )
                      : <span className={styles.noCandidates}>No suggestions — pick manually</span>}
                  </td>
                  <td className={styles.actionsCol}>
                    <div className={styles.actionButtons}>
                      <button
                        type='button'
                        className={styles.linkBtn}
                        onClick={() => openLinkDialog(acc)}
                      >
                        <FontAwesomeIcon icon='link' /> Link Existing
                      </button>
                      <button
                        type='button'
                        className={styles.createBtn}
                        onClick={() => handleCreateProfile(acc, 'create_new')}
                        disabled={creatingProfile === acc.id}
                      >
                        <FontAwesomeIcon icon='plus-circle' /> {creatingProfile === acc.id ? 'Creating…' : 'Create New'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                type='button'
              >
                Previous
              </button>
              <span className={styles.pageInfo}>
                Page {page} of {totalPages}
              </span>
              <button
                className={styles.pageBtn}
                disabled={page >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                type='button'
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {linkDialogAccount && (
        <div className={styles.modalOverlay} onClick={closeLinkDialog}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Link &quot;{linkDialogAccount.account_name}&quot; to a Stripe customer</h3>
              <button type='button' className={styles.modalClose} onClick={closeLinkDialog}>
                <FontAwesomeIcon icon='times' />
              </button>
            </div>
            <div className={styles.modalBody}>
              <input
                type='text'
                className={styles.modalSearchInput}
                placeholder='Search Stripe by name or email…'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
                data-testid='link-search-input'
              />
              {searching && <div className={styles.searchHint}>Searching Stripe…</div>}
              {!searching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                <div className={styles.searchHint}>No Stripe customers match.</div>
              )}
              <div className={styles.searchResults}>
                {searchResults.map(r => (
                  <div key={r.id} className={styles.searchResult}>
                    <div className={styles.searchResultMain}>
                      <span className={styles.searchResultName}>{r.name || '(no name)'}</span>
                      <span className={styles.searchResultEmail}>{r.email || '(no email)'}</span>
                    </div>
                    <div className={styles.searchResultMeta}>
                      <code>{r.id}</code>
                      <button
                        type='button'
                        className={styles.linkBtn}
                        disabled={linking}
                        onClick={() => handleLink(r.id)}
                        data-testid={`link-result-${r.id}`}
                      >
                        {linking ? 'Linking…' : 'Link'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <CsvLinkDialog
        open={csvDialogOpen}
        onClose={() => setCsvDialogOpen(false)}
        onComplete={() => { fetchAccounts(1); setPage(1) }}
        showToast={showToast}
      />

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={closeToast}
      />
    </>
  )

  if (embedded) return content

  return (
    <PageLayout>
      <PageHeader title='Unlinked Accounts' subtitle='Client accounts not yet linked to a Stripe customer' />
      {content}
    </PageLayout>
  )
}

AdminBillingUnlinkedAccounts.propTypes = {
  embedded: PropTypes.bool
}


export default AdminBillingUnlinkedAccounts
